import { useMemo } from 'react';
import { Typography, Button, Tooltip } from 'antd';
import { DollarOutlined } from '@ant-design/icons';
import { SmartDataTableProps } from '@src/components/SmartDataTable/types';
import { useDeliveryStatusRender } from './useDeliveryStatusRender';
import { usePaymentStatusRender } from './usePaymentStatusRender';
import { useUserTimestampRender } from '@src/hooks';
import {
  DELIVERY_STATUS_FILTER_OPTIONS,
  PAYMENT_STATUS_FILTER_OPTIONS,
} from '../constants';
import dayjs from 'dayjs';

const { Text } = Typography;

interface UsePurchaseOrdersColumnsProps {
  onCreateTransaction?: (transactionData: {
    transaction_type: 'po';
    po_id: number;
    amount: number;
    supplier: any; // Add supplier to type definition
    type: string; // Add payment type
  }) => void;
  showCreatedAt?: boolean;
}

export const usePurchaseOrdersColumns = (
  props?: UsePurchaseOrdersColumnsProps
): SmartDataTableProps['columns'] => {
  const { onCreateTransaction, showCreatedAt = true } = props || {};
  const getDeliveryStatusConfig = useDeliveryStatusRender();
  const getPaymentStatusConfig = usePaymentStatusRender();
  const userTimestampRender = useUserTimestampRender({
    size: 'small',
  });

  const handleCreateTransaction = (record: any) => {
    const transactionData = {
      transaction_type: 'po' as const,
      po_id: record.id,
      amount: Number(record.due_amount || 0),
      supplier: record.supplier, // Add supplier
      type: 'MEDICAL_PURCHASES', // Set payment type to MEDICAL_PURCHASES
    };

    if (onCreateTransaction) {
      onCreateTransaction(transactionData);
    }
  };

  return useMemo(() => {
    const baseColumns: SmartDataTableProps['columns'] = [
      {
        key: 'id',
        title: 'ID / Invoice',
        dataIndex: 'id',
        sorter: true,
        minWidth: 120,
        ellipsis: true,
        render: (value: any, record: any) => (
          <div>
            <Text
              style={{
                fontSize: '12px',
                color: '#8c8c8c',
                backgroundColor: '#f5f5f5',
                padding: '2px 6px',
                borderRadius: '4px',
              }}
            >
              #{value}
            </Text>
            {record.invoice_no && (
              <div
                style={{ fontSize: '12px', color: '#262626', marginTop: '2px' }}
              >
                {record.invoice_no}
              </div>
            )}
          </div>
        ),
      },
      {
        key: 'supplier_name',
        title: 'Supplier',
        dataIndex: 'supplier_name',
        sorter: false,
        minWidth: 150,
        ellipsis: true,
        render: (_: any, record: any) => (
          <Text style={{ fontWeight: 500, color: '#262626' }}>
            {record.supplier?.name || 'N/A'}
          </Text>
        ),
      },
      {
        key: 'order_date',
        title: 'Order Date',
        dataIndex: 'order_date',
        sorter: true,
        minWidth: 120,
        render: (date: string) => (
          <div>
            {date ? (
              <div style={{ fontSize: '13px', color: '#262626' }}>
                {dayjs(date).format('DD MMM YYYY')}
              </div>
            ) : (
              '-'
            )}
          </div>
        ),
      },
      {
        key: 'delivery_status',
        title: 'Delivery Status',
        dataIndex: 'delivery_status',
        sorter: true,
        minWidth: 130,
        filterable: true,
        filterType: 'multiselect',
        filterOptions: [...DELIVERY_STATUS_FILTER_OPTIONS],
        render: (status: string) => getDeliveryStatusConfig(status),
      },
      {
        key: 'total_amount',
        title: 'Total Amount',
        dataIndex: 'total_amount',
        sorter: true,
        minWidth: 130,
        render: (amount: number) => (
          <Text style={{ fontWeight: 500, color: '#52c41a' }}>
            ₹
            {amount
              ? Number(amount).toLocaleString('en-IN', {
                  minimumFractionDigits: 2,
                })
              : '0.00'}
          </Text>
        ),
      },
      {
        key: 'due_amount',
        title: 'Due Amount',
        dataIndex: 'due_amount',
        sorter: true,
        minWidth: 130,
        render: (amount: number) => (
          <Text
            style={{
              fontWeight: 500,
              color: amount && Number(amount) > 0 ? '#ff4d4f' : '#52c41a',
            }}
          >
            ₹
            {amount
              ? Number(amount).toLocaleString('en-IN', {
                  minimumFractionDigits: 2,
                })
              : '0.00'}
          </Text>
        ),
      },
      {
        key: 'payment_status',
        title: 'Payment Status',
        dataIndex: 'payment_status',
        sorter: true,
        minWidth: 130,
        filterable: true,
        filterType: 'multiselect',
        filterOptions: [...PAYMENT_STATUS_FILTER_OPTIONS],
        render: (status: string) => getPaymentStatusConfig(status),
      },
      {
        key: 'actions',
        title: 'Payment',
        dataIndex: 'actions',
        minWidth: 120,
        render: (value: any, record: any) => {
          // In Ant Design table render function, the second parameter is the record
          if (!record) return null;

          const dueAmount = Number(record.due_amount || 0);
          const canCreateTransaction = dueAmount > 0;

          if (!canCreateTransaction) return null;

          return (
            <Tooltip
              title={`Create PO Transaction - ₹${dueAmount.toFixed(2)} due`}
            >
              <Button
                type="primary"
                size="small"
                icon={<DollarOutlined />}
                onClick={() => handleCreateTransaction(record)}
                style={{
                  fontSize: '11px',
                  height: '28px',
                  padding: '4px 8px',
                }}
              >
                Pay ₹{dueAmount.toFixed(2)}
              </Button>
            </Tooltip>
          );
        },
      },
      {
        key: 'user_timestamp',
        title: 'User & Timestamp',
        dataIndex: 'user_info',
        sorter: false,
        minWidth: 200,
        render: (value: any, record: any) => userTimestampRender(record),
      },
    ];

    // Conditionally add the created_at column with date range filter
    if (showCreatedAt) {
      baseColumns.push({
        key: 'created_at',
        title: 'Created Date',
        dataIndex: 'created_at',
        sorter: true,
        minWidth: 140,
        filterable: true,
        filterType: 'daterange' as const,
        render: () => null, // Hide the column content but keep filter functionality
        hidden: true, // Hide the column from display
      });
    }

    return baseColumns;
  }, [
    getDeliveryStatusConfig,
    getPaymentStatusConfig,
    userTimestampRender,
    onCreateTransaction,
    showCreatedAt,
  ]);
};
